import { useState, useEffect } from 'react'
import styles from '../style.module.scss'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import Joi from 'joi'
import http from '../../../../../shared/http'
import { TApi, TLanguages, TRoles } from '../../../../../shared/const'
import { schemaOptions } from '../../../../../shared/i18n'
import { joiPhone, NAME_PATTERN, splitDateOnly, toDateOnly } from '../../../../../shared/helpers'
import { joiResolver } from '@hookform/resolvers/joi'
import PropTypes from 'prop-types'

import SelectField from '../../../../../UI/SelectField'
import TextField from '../../../../../UI/TextField'

import Btn from '../../../../../UI/Btn'
import { subYears } from 'date-fns'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { updateProfile } from '../../../../../redux/ducks/user'
import VisibilityAccess from '../../../../VisibilityAccess'

const minAge = subYears(new Date(), 18)
const maxAge = subYears(new Date(), 70)

const schema = Joi.object({
	salutation: Joi.string().required(),
	firstName: Joi.string()
		.min(2)
		.max(26)
		.pattern(NAME_PATTERN, 'name')
		.required(),
	lastName: Joi.string()
		.min(2)
		.max(26)
		.pattern(NAME_PATTERN, 'name')
		.required(),
	day: Joi.any()
		.allow(null)
		.custom((value, helpers) => {
			if (!value) return value

			const day = Number(value)
			if (isNaN(day)) return helpers.error('any.day')

			const { month, year } = helpers.state.ancestors[0]

			if (!month || !year) {
				if (day < 1 || day > 31) return helpers.error('any.day')
				return value
			}

			const daysInMonth = new Date(year, month, 0).getDate()
			if (day < 1 || day > daysInMonth) return helpers.error('any.day')

			return value
		})
		.messages({ 'any.day': 'Invalid day for the selected month/year' }),
	month: Joi.number().min(1).max(12).allow(null),

	year: Joi.number()
		.allow(null)
		.custom((value, helpers) => {
			if (!value) return value

			const year = Number(value)
			if (!year) return helpers.error('any.invalid')

			if (year < maxAge.getFullYear() || year > minAge.getFullYear()) {
				return helpers.error('date.outOfRangeAdult')
			}

			return value
		})
		.messages({
			'date.outOfRangeAdult': `Year must make age between 18 and 65 years`
		}),
	phone: joiPhone,
	titlePrefixed: Joi.string().allow('', null),
	titleSuffixed: Joi.string().allow('', null)
})

function BasicInfo(props) {
	const { t, i18n } = useTranslation()
	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm({
		mode: 'onChange',
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			abortEarly: false,
			...schemaOptions
		})
	})
	const [isLoading, setLoading] = useState(false)

	useEffect(() => {
		reset({
			salutation: props.salutation,
			firstName: props.firstName,
			lastName: props.lastName,
			...splitDateOnly(props.dateOfBirth),
			phone: props.phone,
			titlePrefixed: props.titlePrefixed,
			titleSuffixed: props.titleSuffixed
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props])

	const onSubmit = data => {
		setLoading(true)

		const credentials = {
			firstName: data.firstName,
			lastName: data.lastName,
			gender: data.gender,
			phone: data.phone,
			salutation: data.salutation,

			dateOfBirth:
				data.year && data.month && data.day ? toDateOnly(data.year, data.month, data.day) : null,
			titlePrefixed: data.titlePrefixed,
			titleSuffixed: data.titleSuffixed
		}

		http.put(TApi.ADMIN_PUT_PROFILE, credentials)
			.then(() => {
				props.updateProfile(credentials)
				toast.success(t('Profile has been updated successfully.'))
			})
			.finally(() => setLoading(false))
	}

	return (
		<div className={styles.root}>
			<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<div className={styles.section}>
					<h1 className={styles.sectionTitle}>{t('Basic Info')}</h1>
					<div className={styles.sectionContent}>
						<div className={styles.flexContainer}>
							<SelectField
								required
								disabled={isLoading}
								mb={20}
								label={t('salutation')}
								control={control}
								name="salutation"
								error={errors.salutation}
								placeholder="Salutation"
								options={props.salutations?.map(v => ({
									value: v.value_EN,
									label: i18n.language === TLanguages.EN ? v.value_EN : v.value_DE
								}))}
							/>
							<VisibilityAccess access={[TRoles.ADMIN]}>
								<TextField
									disabled
									mb={20}
									label={t('state')}
									placeholder={props.state}
									value={props.state}
								/>
							</VisibilityAccess>
						</div>
						<div className={styles.flexContainer}>
							<TextField
								required
								disabled={isLoading}
								mb={20}
								label={t('First Name')}
								placeholder={props.firstName}
								error={errors.firstName}
								register={register('firstName')}
							/>
							<TextField
								required
								disabled={isLoading}
								mb={20}
								label={t('Last Name')}
								placeholder={props.lastName}
								error={errors.lastName}
								register={register('lastName')}
							/>
						</div>

						<div className={styles.flexContainer}>
							<TextField
								disabled={props.isLoading}
								mb={20}
								label={t('Title Prefixed')}
								error={errors.titlePrefixed}
								placeholder={t('Enter Title')}
								register={register('titlePrefixed')}
							/>
							<TextField
								disabled={props.isLoading}
								mb={20}
								label={t('Title Suffixed')}
								placeholder={t('Enter Title')}
								error={errors.titleSuffixed}
								register={register('titleSuffixed')}
							/>
						</div>
						<div className={styles.flexContainer}>
							<div style={{ position: 'relative' }}>
								<div className={styles.dateOfBirthWrapper}>
									<TextField
										disabled={isLoading}
										mb={20}
										label={t('day')}
										placeholder={t('dd')}
										register={register('day')}
									/>
									<TextField
										disabled={isLoading}
										mb={20}
										label={t('month')}
										placeholder={t('mm')}
										register={register('month')}
									/>
									<TextField
										disabled={isLoading}
										mb={20}
										label={t('year')}
										placeholder={t('yyyy')}
										register={register('year')}
									/>
								</div>
								{(errors.year || errors.day || errors.month) && (
									<div className={styles.errorsWrapper}>
										{errors.day && <p className={styles.error}>{errors.day.message}</p>}
										{errors.month && <p className={styles.error}>{errors.month.message}</p>}
										{errors.year && <p className={styles.error}>{errors.year.message}</p>}
									</div>
								)}
							</div>
							<TextField
								required
								disabled={isLoading}
								mb={20}
								label={t('phone')}
								placeholder="+43 6XX XXXXXX"
								error={errors.phone}
								register={register('phone')}
							/>
						</div>
						<div className={styles.btnContainer}>
							<Btn
								loading={isLoading}
								className={clsx(styles.btn)}
								type="submit"
								icon="arrow"
								iconSize={{ w: 10, h: 10 }}
								iconColor="#fff">
								{t('save')}
							</Btn>
						</div>
					</div>
				</div>
			</form>
		</div>
	)
}
BasicInfo.propTypes = {
	salutation: PropTypes.string.isRequired,
	firstName: PropTypes.string.isRequired,
	lastName: PropTypes.string.isRequired,
	dateOfBirth: PropTypes.string,
	phone: PropTypes.string.isRequired,
	state: PropTypes.string.isRequired,
	titleSuffixed: PropTypes.string.isRequired,
	titlePrefixed: PropTypes.string.isRequired,
	salutations: PropTypes.array.isRequired
}
const mapStateToProps = state => ({
	salutation: state.user.profile.salutation,
	firstName: state.user.profile.firstName,
	lastName: state.user.profile.lastName,
	dateOfBirth: state.user.profile.dateOfBirth,
	phone: state.user.profile.phone || '',
	state: state.user.profile.state,
	titleSuffixed: state.user.profile.titleSuffixed,
	titlePrefixed: state.user.profile.titlePrefixed,
	salutations: state.options.salutations
})

const mapDispatchToProps = dispatch => ({
	updateProfile: data => dispatch(updateProfile(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(BasicInfo)
